<template>
  <calendar-header
    :daily="daily"
    :date="date"
  />
  <div class="content">
    <frame :lines="lines"/>
    <div class="calendar">
      <hours-column
          class="column"
          :start-hour="startHour"
          :end-hour="endHour"
          :step="step"
      />
      <div v-if="!daily">
        <div
          v-if="dataReady"
          class="days"
        >
          <column
            v-for="dayOfTheWeek in 7"
            :key="dayOfTheWeek"
            :date="date.isoWeekday(dayOfTheWeek)"
            :data="preparedData[dayOfTheWeek - 1]"
            :daily="daily"
            :start-hour="startHour"
            :end-hour="endHour"
            :step="step"
            @refreshCalendar="getData"
          />
        </div>
        <div
          v-else
          class="loader-container"
        >
          <ring-loader
            :color="loaderColor"
            :size="loaderSize"
          />
        </div>
      </div>
      <div v-else>
        <div
          v-if="dataReady"
          class="days"
        >
          <column
            v-for="trainerNumber in data.length"
            :key="trainerNumber"
            :daily="daily"
            :date="date"
            :data="preparedData[trainerNumber - 1]"
            :start-hour="startHour"
            :end-hour="endHour"
            :step="step"
            @refreshCalendar="getData"
          />
        </div>
        <div
          v-else
          class="loader-container"
        >
          <ring-loader
            :color="loaderColor"
            :size="loaderSize"
          />
        </div>
      </div>
      <div class="placeholder" />
      <calendar-menu
        @showNavigationModal="changeShowNavigationModal"
        @showPaymentModal="changeShowPaymentModal"
      />
      <teleport v-if="showNavigationModal" to="body">
        <navigation-modal
            @closeNavigationModal="changeShowNavigationModal"
            :date="date"
            @newDate="changeDate"
            v-bind="$attrs"
        />
      </teleport>
      <teleport v-if="showPaymentModal" to="body">
        <payment-modal
          @closePaymentModal="changeShowPaymentModal"
        />
      </teleport>
    </div>
  </div>
</template>

<script>
import {
  computed,
  onBeforeUnmount, reactive, ref, watch,
} from 'vue';

import dayjs from 'dayjs';
import axios from 'axios';

import RingLoader from 'vue-spinner/src/RingLoader.vue';

import store from '@/store';

import { setUserData } from '@/plugins/auth';

import { urlBuilder } from '@/plugins/helpers';

import CalendarMenu from '../components/calendar/Menu.vue';
import Column from '../components/calendar/Column.vue';
import Frame from '../components/calendar/Frame.vue';
import HoursColumn from '../components/calendar/HoursColumn.vue';
import NavigationModal from '../components/modal/NavigationModal.vue';
import CalendarHeader from '../components/calendar/CalendarHeader.vue';
import PaymentModal from '../components/modal/PaymentModal.vue';

require('dayjs/locale/pl');
dayjs.extend(require('dayjs/plugin/customParseFormat'));
dayjs.extend(require('dayjs/plugin/weekday'));
dayjs.extend(require('dayjs/plugin/isoWeek'));

export default {
  name: 'Calendar',

  components: {
    CalendarHeader,
    CalendarMenu,
    Column,
    Frame,
    HoursColumn,
    NavigationModal,
    PaymentModal,
    RingLoader,
  },

  setup() {
    const startHour = 8;
    const endHour = 22;
    const step = 30;
    const lines = ((endHour - startHour) * 60) / step + 1;
    const loaderColor = '#34495E';
    const loaderSize = '100px';

    const data = ref([]);
    const dataReady = ref(false);
    const date = ref(dayjs());
    const preparedData = ref([]);

    const daily = computed(() => store.getters.daily);
    const selectedTrainerId = computed(() => store.getters.selectedTrainerId);

    const arrowStep = computed(() => {
      let value = 7;
      if (daily.value) value = 1;
      return value;
    });

    setUserData();

    const usedDates = reactive({
      start_date: {},
      end_date: {},
    });

    async function getData() {
      dataReady.value = false;
      let params = null;
      if (daily.value) {
        usedDates.start_date = date.value.format('YYYY-MM-DD');
        usedDates.end_date = date.value.format('YYYY-MM-DD');
        params = {
          start_date: date.value.format('DD-MM-YYYY'),
          end_date: date.value.format('DD-MM-YYYY'),
        };
        data.value = (
          await axios.get(urlBuilder('/trainer-events/', params))
        ).data;
      } else if (selectedTrainerId.value) {
        usedDates.start_date = `${date.value.isoWeekday(1).format('YYYY-MM-DD')}`;
        usedDates.end_date = `${date.value.isoWeekday(7).format('YYYY-MM-DD')}`;
        params = {
          start_date: `${date.value.isoWeekday(1).format('DD-MM-YYYY')}`,
          end_date: `${date.value.isoWeekday(7).format('DD-MM-YYYY')}`,
        };
        data.value = (
          await axios.get(urlBuilder(`/trainer-events/${selectedTrainerId.value}/`, params))
        ).data;
      }
    }

    watch(() => daily.value || selectedTrainerId.value, () => {
      getData();
    });

    getData();

    function prepareTime(currentDate, time) {
      return dayjs(currentDate)
        .hour(time.substring(0, 2))
        .minute(time.substring(3, 5));
    }

    function prepareTimeWindow(day) {
      return day.time_windows ? day.time_windows.map((timeWindow) => ({
        start_time: prepareTime(day.date, timeWindow.start_time),
        end_time: prepareTime(day.date, timeWindow.end_time),
      })) : [];
    }

    function convertData() {
      preparedData.value = data.value.days.map((day) => ({
        date: dayjs(day.date),
        events: day.events.map((event) => ({
          start_time: prepareTime(day.date, event.start_time),
          end_time: prepareTime(day.date, event.end_time),
          id: event.id,
          name: event.name,
          color: event.color,
          isEmpty: event.is_empty,
          default_name: event.default_name,
          isApproved: event.is_approved,
          isPeriodic: event.is_periodic,
          smsSentCount: event.sms_sent_count || 0,
          smsConfirmedCount: event.sms_confirmed_count || 0,
          clientsCount: event.clients_count || 0,
        })),
        time_windows: prepareTimeWindow(day),
      }));
    }

    function convertDailyData() {
      preparedData.value = data.value.map((trainer) => ({
        date: dayjs(trainer.days[0].date),
        events: trainer.days[0].events.map((event) => ({
          start_time: prepareTime(trainer.days[0].date, event.start_time),
          end_time: prepareTime(trainer.days[0].date, event.end_time),
          id: event.id,
          name: event.name,
          color: event.color,
          isEmpty: event.is_empty,
          default_name: event.default_name,
          isApproved: event.is_approved,
          isPeriodic: event.is_periodic,
          smsSentCount: event.sms_sent_count || 0,
          smsConfirmedCount: event.sms_confirmed_count || 0,
          clientsCount: event.clients_count || 0,
        })),
        trainer: trainer.full_name,
        id: trainer.id,
        time_windows: prepareTimeWindow(trainer.days[0]),
      }));
    }

    function isCorrectDate() {
      let result = false;
      if (daily.value) {
        result = (data.value[0].start_date === usedDates.start_date
        && data.value[0].end_date === usedDates.end_date);
      } else {
        result = (data.value.start_date === usedDates.start_date
        && data.value.end_date === usedDates.end_date);
      }
      return result;
    }

    watch(() => data.value, () => {
      if (isCorrectDate()) {
        if (data.value && !daily.value) convertData();
        else if (data.value && daily.value) convertDailyData();
        dataReady.value = true;
      }
    });

    async function prev() {
      date.value = date.value.subtract(arrowStep.value, 'day');
      getData();
      await store.dispatch('resetChangeEvent');
    }

    async function next() {
      date.value = date.value.add(arrowStep.value, 'day');
      getData();
      await store.dispatch('resetChangeEvent');
    }

    function today() {
      date.value = dayjs();
      getData();
    }

    function arrowButtonListener(event) {
      if (event.key === 'ArrowLeft') prev();
      else if (event.key === 'ArrowRight') next();
      else if (event.key === 'Escape') today();
    }

    window.addEventListener('keydown', arrowButtonListener);

    onBeforeUnmount(() => window.removeEventListener('keydown', arrowButtonListener));

    const showNavigationModal = ref(false);
    function changeShowNavigationModal() {
      showNavigationModal.value = !showNavigationModal.value;
    }

    async function changeDate(event) {
      date.value = dayjs(event, 'DD-MM-YYYY');
      getData();
      await store.dispatch('resetChangeEvent');
    }

    const showPaymentModal = ref(false);
    function changeShowPaymentModal() {
      showPaymentModal.value = !showPaymentModal.value;
    }

    return {
      changeDate,
      changeShowNavigationModal,
      changeShowPaymentModal,
      daily,
      dataReady,
      data,
      date,
      endHour,
      getData,
      lines,
      loaderColor,
      loaderSize,
      preparedData,
      showNavigationModal,
      showPaymentModal,
      startHour,
      step,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '../assets/css/colors.scss';

  .calendar {
    display: flex;
    max-width: calc(100vw - 15px);
    min-width: 850px;
    position: relative;
    top: 0;
  }

  .days {
    display: flex;
  }

  .loader-container {
    align-items: center;
    bottom: 50%;
    display: flex;
    justify-content: center;
    left: 50%;
    position: fixed;
    right: 50%;
    top: 50%;
    z-index: 20;
  }

  .placeholder {
    background: $white;
    height: 60px;
    position: sticky;
    top: 0;
    width: 100%;
    z-index: 20;
  }

</style>
